/**
 * Injects color swatches into Globo-rendered product cards.
 * Uses Section Rendering API for swatch colors/images (Shopify native metadata)
 * and /products/{handle}.js for variant image mapping.
 */
(function () {
  const COLOR_NAMES = ['color', 'colour', 'カラー', '色'];
  const MAX_SWATCHES = 5;
  const processedCards = new WeakSet();
  const productCache = new Map();
  const swatchCache = new Map();

  function findColorOption(product) {
    if (!product.options) return null;
    for (const opt of product.options) {
      const name = (typeof opt === 'string' ? opt : opt.name || '').toLowerCase();
      if (COLOR_NAMES.includes(name)) return opt;
    }
    return null;
  }

  async function fetchProductData(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache.set(handle, data);
      return data;
    } catch (e) {
      return null;
    }
  }

  async function fetchSwatchData(handle) {
    if (swatchCache.has(handle)) return swatchCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}?section_id=product-swatch-data`);
      if (!res.ok) {
        swatchCache.set(handle, null);
        return null;
      }
      const html = await res.text();
      const match = html.match(/<script[^>]*data-product-swatches[^>]*>([\s\S]*?)<\/script>/);
      if (!match) {
        swatchCache.set(handle, null);
        return null;
      }
      const data = JSON.parse(match[1]);
      swatchCache.set(handle, data);
      return data;
    } catch (e) {
      swatchCache.set(handle, null);
      return null;
    }
  }

  function buildSwatchMap(shopifyProduct, colorOptionName) {
    const map = {};
    const optionIndex = shopifyProduct.options.findIndex((o) => {
      const name = typeof o === 'string' ? o : o.name || '';
      return name.toLowerCase() === colorOptionName.toLowerCase();
    });
    if (optionIndex === -1) return map;

    const seen = new Set();
    for (const variant of shopifyProduct.variants) {
      const colorValue = variant[`option${optionIndex + 1}`];
      if (!colorValue || seen.has(colorValue)) continue;
      seen.add(colorValue);
      const imgSrc = variant.featured_image?.src;
      let imageUrl = null;
      if (imgSrc) {
        imageUrl = imgSrc.includes('width=') ? imgSrc : imgSrc + (imgSrc.includes('?') ? '&' : '?') + 'width=940';
      }
      map[colorValue] = {
        variantId: variant.id,
        image: imageUrl,
        width: variant.featured_image?.width || null,
      };
    }
    return map;
  }

  function createSwatchElement(card, swatchMap, swatchData, productUrl) {
    const area = card.querySelector('.product-card__swatches-area');
    if (!area) return;

    const values = Object.keys(swatchMap);
    if (values.length < 1) return;

    const swatchesEl = document.createElement('card-swatches');
    swatchesEl.className = 'card-swatches no-js-hidden';
    swatchesEl.dataset.productUrl = productUrl;

    // Embed swatch map as JSON
    const script = document.createElement('script');
    script.type = 'application/json';
    script.dataset.swatchMap = '';
    script.textContent = JSON.stringify(swatchMap);
    swatchesEl.appendChild(script);

    // Create swatch buttons
    const visibleCount = Math.min(values.length, MAX_SWATCHES);
    for (let i = 0; i < visibleCount; i++) {
      const value = values[i];
      const swatch = swatchData?.[value];

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'card-swatches__item' + (i === 0 ? ' card-swatches__item--active' : '');
      btn.setAttribute('aria-label', value);
      btn.dataset.swatchValue = value;

      const span = document.createElement('span');
      if (swatch?.value) {
        span.className = 'swatch';
        if (swatch.type === 'image') {
          span.style.setProperty('--swatch--background', 'url(' + swatch.value + ')');
        } else {
          span.style.setProperty('--swatch--background', swatch.value);
        }
      } else {
        span.className = 'swatch swatch--unavailable';
      }
      btn.appendChild(span);
      swatchesEl.appendChild(btn);
    }

    // Overflow indicator
    const remaining = values.length - visibleCount;
    if (remaining > 0) {
      const overflow = document.createElement('span');
      overflow.className = 'card-swatches__overflow';
      overflow.textContent = `+${remaining}`;
      swatchesEl.appendChild(overflow);
    }

    area.appendChild(swatchesEl);
  }

  async function processCard(card) {
    if (processedCards.has(card)) return;
    processedCards.add(card);

    const handle = card.dataset.productHandle;
    if (!handle) return;

    // Find color option from Globo state
    const globoProduct = window.globofilter?.state?.products?.find(
      (p) => p.handle === handle
    );
    if (!globoProduct) return;

    const colorOption = findColorOption(globoProduct);
    if (!colorOption) return;

    const colorOptionName = typeof colorOption === 'string' ? colorOption : colorOption.name;
    const colorValues = typeof colorOption === 'string' ? [] : colorOption.values || [];
    if (colorValues.length < 1) return;

    // Fetch product data and swatch metadata in parallel
    const [shopifyProduct, swatchData] = await Promise.all([
      fetchProductData(handle),
      fetchSwatchData(handle),
    ]);
    if (!shopifyProduct) return;

    const swatchMap = buildSwatchMap(shopifyProduct, colorOptionName);
    if (Object.keys(swatchMap).length < 1) return;

    const productUrl = card.querySelector('.product-card__link')?.getAttribute('href') || `/products/${handle}`;
    createSwatchElement(card, swatchMap, swatchData, productUrl);
  }

  function processAllCards() {
    const cards = document.querySelectorAll('.product-card[data-product-handle]');
    cards.forEach((card) => processCard(card));
  }

  // Observe DOM for Globo-rendered cards
  const observer = new MutationObserver((mutations) => {
    let hasNewCards = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          if (node.matches?.('.product-card[data-product-handle]') ||
              node.querySelector?.('.product-card[data-product-handle]')) {
            hasNewCards = true;
            break;
          }
        }
      }
      if (hasNewCards) break;
    }
    if (hasNewCards) {
      clearTimeout(observer._timeout);
      observer._timeout = setTimeout(processAllCards, 200);
    }
  });

  // Start observing
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
      processAllCards();
    });
  } else {
    observer.observe(document.body, { childList: true, subtree: true });
    processAllCards();
  }

  // Also listen for Globo filter events
  document.addEventListener('globofilter:afterRender', processAllCards);
  document.addEventListener('globofilter.afterRender', processAllCards);

  // Fallback: re-check after Globo may have finished rendering
  setTimeout(processAllCards, 1500);
  setTimeout(processAllCards, 3000);
})();
